// src/cockpit4/components/ResourceManager.tsx
// ============================================
// P1-功能4：资源管理与调配（项目中心）
//
// 功能：
//   1. 团队成员负载看板（成员列表 + 当前任务数 + 负载率百分比 + 负载条）
//   2. 资源冲突识别（同一成员多个任务截止日重叠时高亮提醒）
//   3. AI智能排期和资源分配建议（基于任务优先级和成员负载推荐分配）
//   4. 人力成本核算（成员时薪 × 任务工时 = 成本，总成本统计）
//   5. 资源利用率统计（已分配工时/总可用工时）
//   6. 技能矩阵（成员 × 技能 的表格，标记熟练度）
//
// 数据来源：
//   - localStorage 持久化的团队成员（xeaos_team_members）
//   - localStorage 读取的任务列表（xeaos_tasks_list，复用 TaskBoard）
// ============================================

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users,
  Plus,
  X,
  RefreshCw,
  Loader2,
  Trash2,
  Pencil,
  AlertTriangle,
  Sparkles,
  DollarSign,
  Gauge,
  Grid3x3,
  Lightbulb,
  User,
  Clock,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

// ============================================
// 类型定义
// ============================================

/** 技能熟练度 */
export type SkillLevel = "beginner" | "intermediate" | "expert";

/** 团队成员模型 */
export interface TeamMember {
  id: string;
  name: string;
  role: string;
  /** 时薪（元/小时） */
  hourlyRate: number;
  /** 每周可用工时 */
  weeklyCapacityHours: number;
  /** 技能清单：技能名 → 熟练度 */
  skills: Record<string, SkillLevel>;
  createdAt: string;
  updatedAt: string;
}

/** 复用 TaskBoard 的任务结构（只读取需要的字段） */
interface TaskLite {
  id: string;
  title: string;
  assignee: string;
  priority: "low" | "medium" | "high" | "urgent";
  status: "todo" | "in_progress" | "done" | "blocked";
  dueDate: string;
  createdAt: string;
  actualProgress: number;
}

// ============================================
// 常量配置
// ============================================

const MEMBERS_KEY = "xeaos_team_members";
const TASKS_KEY = "xeaos_tasks_list";

/** 按优先级估算单任务工时（小时） */
const PRIORITY_HOURS: Record<TaskLite["priority"], number> = {
  low: 4,
  medium: 8,
  high: 12,
  urgent: 16,
};

const PRIORITY_META: Record<
  TaskLite["priority"],
  { label: string; color: string }
> = {
  low: { label: "低", color: "#6B7280" },
  medium: { label: "中", color: "#3B82F6" },
  high: { label: "高", color: "#F59E0B" },
  urgent: { label: "紧急", color: "#EF4444" },
};

const SKILL_LEVEL_META: Record<
  SkillLevel,
  { label: string; color: string; symbol: string }
> = {
  beginner: { label: "入门", color: "#6B7280", symbol: "●" },
  intermediate: { label: "熟练", color: "#3B82F6", symbol: "●●" },
  expert: { label: "专家", color: "#10B981", symbol: "●●●" },
};

// ============================================
// localStorage 持久化
// ============================================

/** 加载团队成员 */
function loadMembers(): TeamMember[] {
  try {
    const raw = localStorage.getItem(MEMBERS_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw) as TeamMember[];
    if (!Array.isArray(list)) return [];
    return list;
  } catch {
    return [];
  }
}

/** 保存团队成员 */
function saveMembers(members: TeamMember[]): void {
  try {
    localStorage.setItem(MEMBERS_KEY, JSON.stringify(members));
  } catch {
    // 静默降级
  }
}

/** 加载任务列表（复用 TaskBoard 的存储） */
function loadTasks(): TaskLite[] {
  try {
    const raw = localStorage.getItem(TASKS_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw) as TaskLite[];
    if (!Array.isArray(list)) return [];
    return list;
  } catch {
    return [];
  }
}

// ============================================
// 工具函数
// ============================================

/** 格式化日期 */
function formatDate(iso: string | undefined): string {
  if (!iso) return "—";
  return iso.slice(0, 10);
}

/** 计算两个日期是否在 3 天内重叠（用于资源冲突识别） */
function isDateClose(a: string, b: string, rangeDays = 3): boolean {
  const ta = new Date(a).getTime();
  const tb = new Date(b).getTime();
  if (isNaN(ta) || isNaN(tb)) return false;
  return Math.abs(ta - tb) <= rangeDays * 24 * 60 * 60 * 1000;
}

/** 计算成员负载率（已分配工时 / 周可用工时 × 100%） */
function calcLoadRate(assignedHours: number, weeklyCapacity: number): number {
  if (weeklyCapacity <= 0) return 0;
  return Math.round((assignedHours / weeklyCapacity) * 100);
}

/** 根据负载率返回颜色 */
function loadColor(rate: number): string {
  if (rate >= 100) return "#EF4444";
  if (rate >= 80) return "#F59E0B";
  if (rate >= 50) return "#3B82F6";
  return "#10B981";
}

// ============================================
// 主组件
// ============================================

export default function ResourceManager() {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [tasks, setTasks] = useState<TaskLite[]>([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
  const [view, setView] = useState<"workload" | "skills" | "utilization">(
    "workload"
  );

  // ============================================
  // 加载数据
  // ============================================
  const loadData = useCallback(() => {
    setLoading(true);
    setMembers(loadMembers());
    setTasks(loadTasks());
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ============================================
  // 成员 CRUD
  // ============================================
  const handleSaveMember = useCallback(
    (data: Omit<TeamMember, "id" | "createdAt" | "updatedAt">) => {
      const now = new Date().toISOString();
      if (editingMember) {
        const updated = members.map((m) =>
          m.id === editingMember.id
            ? { ...m, ...data, updatedAt: now }
            : m
        );
        setMembers(updated);
        saveMembers(updated);
      } else {
        const newMember: TeamMember = {
          ...data,
          id: `member_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          createdAt: now,
          updatedAt: now,
        };
        const updated = [newMember, ...members];
        setMembers(updated);
        saveMembers(updated);
      }
      setShowEditModal(false);
      setEditingMember(null);
    },
    [editingMember, members]
  );

  const handleDeleteMember = useCallback(
    (memberId: string) => {
      const updated = members.filter((m) => m.id !== memberId);
      setMembers(updated);
      saveMembers(updated);
    },
    [members]
  );

  // ============================================
  // 负载与冲突分析
  // ============================================

  /** 每个成员的负载统计 */
  const memberStats = useMemo(() => {
    const map = new Map<
      string,
      {
        member: TeamMember;
        activeTasks: TaskLite[];
        assignedHours: number;
        loadRate: number;
        cost: number;
      }
    >();

    members.forEach((m) => {
      const activeTasks = tasks.filter(
        (t) =>
          t.assignee === m.name &&
          t.status !== "done"
      );
      const assignedHours = activeTasks.reduce(
        (sum, t) => sum + PRIORITY_HOURS[t.priority],
        0
      );
      const loadRate = calcLoadRate(assignedHours, m.weeklyCapacityHours);
      // 成本 = 已分配工时 × 时薪
      const cost = assignedHours * m.hourlyRate;
      map.set(m.id, { member: m, activeTasks, assignedHours, loadRate, cost });
    });

    return map;
  }, [members, tasks]);

  /** 资源冲突识别：同一成员多个任务截止日相近 */
  const conflicts = useMemo(() => {
    const list: Array<{
      member: TeamMember;
      taskA: TaskLite;
      taskB: TaskLite;
    }> = [];
    memberStats.forEach(({ member, activeTasks }) => {
      for (let i = 0; i < activeTasks.length; i++) {
        for (let j = i + 1; j < activeTasks.length; j++) {
          if (isDateClose(activeTasks[i].dueDate, activeTasks[j].dueDate)) {
            list.push({
              member,
              taskA: activeTasks[i],
              taskB: activeTasks[j],
            });
          }
        }
      }
    });
    return list;
  }, [memberStats]);

  /** AI智能排期建议：未分配任务 → 推荐负载最低的成员 */
  const aiSuggestions = useMemo(() => {
    const unassigned = tasks.filter(
      (t) => !t.assignee && t.status !== "done"
    );
    if (unassigned.length === 0 || members.length === 0) return [];

    return unassigned
      .slice()
      .sort((a, b) => {
        // 紧急度优先
        const order = { urgent: 0, high: 1, medium: 2, low: 3 } as const;
        return order[a.priority] - order[b.priority];
      })
      .map((task) => {
        // 找负载最低的成员
        const sorted = Array.from(memberStats.values()).sort(
          (a, b) => a.loadRate - b.loadRate
        );
        const recommended = sorted[0];
        return { task, recommended };
      });
  }, [tasks, members, memberStats]);

  /** 资源利用率图表数据 */
  const utilizationData = useMemo(() => {
    return members.map((m) => {
      const stat = memberStats.get(m.id);
      const assigned = stat?.assignedHours ?? 0;
      return {
        name: m.name,
        assigned,
        capacity: m.weeklyCapacityHours,
        loadRate: stat?.loadRate ?? 0,
      };
    });
  }, [members, memberStats]);

  /** 全局统计 */
  const globalStats = useMemo(() => {
    const totalCost = Array.from(memberStats.values()).reduce(
      (sum, s) => sum + s.cost,
      0
    );
    const totalAssigned = Array.from(memberStats.values()).reduce(
      (sum, s) => sum + s.assignedHours,
      0
    );
    const totalCapacity = members.reduce(
      (sum, m) => sum + m.weeklyCapacityHours,
      0
    );
    const avgLoad =
      members.length > 0
        ? Math.round(
            Array.from(memberStats.values()).reduce(
              (sum, s) => sum + s.loadRate,
              0
            ) / members.length
          )
        : 0;
    return {
      totalCost,
      totalAssigned,
      totalCapacity,
      avgLoad,
      overloadCount: Array.from(memberStats.values()).filter(
        (s) => s.loadRate >= 100
      ).length,
    };
  }, [memberStats, members]);

  /** 所有技能集合（用于技能矩阵列） */
  const allSkills = useMemo(() => {
    const set = new Set<string>();
    members.forEach((m) => Object.keys(m.skills).forEach((s) => set.add(s)));
    return Array.from(set).sort();
  }, [members]);

  // ============================================
  // 渲染
  // ============================================
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      {/* 顶部光带 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-emerald-500/50 to-transparent" />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md flex items-center justify-center bg-emerald-500/15 border border-emerald-500/30">
            <Users size={13} className="text-emerald-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">
              资源管理与调配
            </h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              Resource Manager · {members.length} 成员 · 平均负载{" "}
              {globalStats.avgLoad}% · 过载 {globalStats.overloadCount}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* 视图切换 */}
          <div className="flex items-center rounded-md border border-white/[0.06] overflow-hidden">
            <button
              onClick={() => setView("workload")}
              className={`px-2 py-1 text-[9px] font-mono uppercase tracking-wider transition-colors ${
                view === "workload"
                  ? "bg-emerald-500/20 text-emerald-300"
                  : "bg-white/[0.02] text-gray-500 hover:bg-white/[0.04]"
              }`}
            >
              负载
            </button>
            <button
              onClick={() => setView("skills")}
              className={`px-2 py-1 text-[9px] font-mono uppercase tracking-wider transition-colors ${
                view === "skills"
                  ? "bg-emerald-500/20 text-emerald-300"
                  : "bg-white/[0.02] text-gray-500 hover:bg-white/[0.04]"
              }`}
            >
              技能矩阵
            </button>
            <button
              onClick={() => setView("utilization")}
              className={`px-2 py-1 text-[9px] font-mono uppercase tracking-wider transition-colors ${
                view === "utilization"
                  ? "bg-emerald-500/20 text-emerald-300"
                  : "bg-white/[0.02] text-gray-500 hover:bg-white/[0.04]"
              }`}
            >
              利用率
            </button>
          </div>

          {/* 新增成员 */}
          <button
            onClick={() => {
              setEditingMember(null);
              setShowEditModal(true);
            }}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-500/15 border border-emerald-500/30 hover:bg-emerald-500/25 transition-colors"
          >
            <Plus size={11} className="text-emerald-400" />
            <span className="text-[9px] font-mono text-emerald-300 uppercase tracking-wider">
              新增成员
            </span>
          </button>

          <button
            onClick={loadData}
            disabled={loading}
            className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-white/[0.04] border border-white/[0.06] hover:bg-white/[0.08] transition-colors disabled:opacity-50"
          >
            {loading ? (
              <Loader2 size={11} className="text-gray-400 animate-spin" />
            ) : (
              <RefreshCw size={11} className="text-gray-400" />
            )}
          </button>
        </div>
      </div>

      {/* KPI 行 */}
      <div className="grid grid-cols-4 gap-2 px-4 py-2 border-b border-white/[0.04]">
        <KpiCell
          label="成员总数"
          value={String(members.length)}
          color="#34D399"
        />
        <KpiCell
          label="已分配工时"
          value={`${globalStats.totalAssigned}h`}
          color="#3B82F6"
        />
        <KpiCell
          label="总可用工时"
          value={`${globalStats.totalCapacity}h`}
          color="#A78BFA"
        />
        <KpiCell
          label="人力成本"
          value={`¥${globalStats.totalCost.toLocaleString()}`}
          color="#FBBF24"
        />
      </div>

      {/* 资源冲突提醒 */}
      {conflicts.length > 0 && (
        <div className="flex items-center gap-1.5 px-4 py-1.5 border-b border-white/[0.04] bg-red-500/[0.04]">
          <AlertTriangle size={10} className="text-red-400" />
          <span className="text-[9px] font-mono text-red-400/90 uppercase tracking-wider">
            检测到 {conflicts.length} 个资源冲突（任务截止日重叠）
          </span>
        </div>
      )}

      {/* AI 排期建议 */}
      {aiSuggestions.length > 0 && (
        <div className="px-4 py-2 border-b border-white/[0.04] bg-purple-500/[0.03]">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Sparkles size={10} className="text-purple-400" />
            <span className="text-[9px] font-mono text-purple-400 uppercase tracking-wider">
              AI 智能排期建议（{aiSuggestions.length} 个未分配任务）
            </span>
          </div>
          <div className="space-y-1 max-h-[120px] overflow-y-auto">
            {aiSuggestions.slice(0, 5).map(({ task, recommended }) => {
              const pMeta = PRIORITY_META[task.priority];
              return (
                <div
                  key={task.id}
                  className="flex items-center gap-2 px-2 py-1 rounded bg-white/[0.02] border border-white/[0.04]"
                >
                  <span
                    className="px-1 rounded text-[8px] font-mono uppercase"
                    style={{
                      background: `${pMeta.color}20`,
                      color: pMeta.color,
                    }}
                  >
                    {pMeta.label}
                  </span>
                  <span className="text-[10px] text-gray-300 truncate flex-1">
                    {task.title}
                  </span>
                  <Lightbulb size={9} className="text-purple-400" />
                  <span className="text-[9px] font-mono text-purple-300">
                    → {recommended.member.name}（负载 {recommended.loadRate}%）
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 主体内容 */}
      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 size={20} className="animate-spin text-emerald-400" />
            <span className="ml-2 text-[11px] font-mono text-gray-500">
              加载资源数据...
            </span>
          </div>
        ) : members.length === 0 ? (
          <EmptyState
            onAdd={() => {
              setEditingMember(null);
              setShowEditModal(true);
            }}
          />
        ) : view === "workload" ? (
          /* 负载看板视图 */
          <div className="space-y-1.5 max-h-[520px] overflow-y-auto">
            {members.map((m) => {
              const stat = memberStats.get(m.id)!;
              const color = loadColor(stat.loadRate);
              const hasConflict = conflicts.some((c) => c.member.id === m.id);
              return (
                <MemberLoadRow
                  key={m.id}
                  member={m}
                  stat={stat}
                  color={color}
                  hasConflict={hasConflict}
                  onEdit={() => {
                    setEditingMember(m);
                    setShowEditModal(true);
                  }}
                  onDelete={() => handleDeleteMember(m.id)}
                />
              );
            })}
          </div>
        ) : view === "skills" ? (
          /* 技能矩阵视图 */
          <SkillMatrix
            members={members}
            skills={allSkills}
            onEdit={(m) => {
              setEditingMember(m);
              setShowEditModal(true);
            }}
          />
        ) : (
          /* 利用率图表视图 */
          <div className="space-y-3">
            <div className="flex items-center gap-1.5">
              <Gauge size={11} className="text-emerald-400" />
              <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
                资源利用率（已分配 / 可用工时）
              </span>
            </div>
            {utilizationData.length === 0 ? (
              <div className="text-center text-[10px] font-mono text-gray-600 py-8">
                暂无数据
              </div>
            ) : (
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={utilizationData}>
                    <CartesianGrid stroke="#ffffff10" strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      tick={{ fill: "#9CA3AF", fontSize: 10, fontFamily: "monospace" }}
                      axisLine={{ stroke: "#ffffff20" }}
                    />
                    <YAxis
                      tick={{ fill: "#9CA3AF", fontSize: 10, fontFamily: "monospace" }}
                      axisLine={{ stroke: "#ffffff20" }}
                    />
                    <Tooltip
                      contentStyle={{
                        background: "#0F1623",
                        border: "1px solid #ffffff20",
                        borderRadius: 6,
                        fontSize: 11,
                        fontFamily: "monospace",
                      }}
                      labelStyle={{ color: "#E5E7EB" }}
                    />
                    <Bar
                      dataKey="assigned"
                      name="已分配工时"
                      fill="#34D399"
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      dataKey="capacity"
                      name="可用工时"
                      fill="#ffffff20"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 成员编辑弹窗 */}
      <AnimatePresence>
        {showEditModal && (
          <MemberEditModal
            member={editingMember}
            onSubmit={handleSaveMember}
            onClose={() => {
              setShowEditModal(false);
              setEditingMember(null);
            }}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ============================================
// KPI 单元格
// ============================================
function KpiCell({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color: string;
}) {
  return (
    <div className="rounded-lg border border-white/[0.04] bg-white/[0.01] px-3 py-1.5">
      <div className="text-[8px] font-mono text-gray-500 uppercase tracking-wider">
        {label}
      </div>
      <div
        className="text-[16px] font-mono font-semibold mt-0.5"
        style={{ color }}
      >
        {value}
      </div>
    </div>
  );
}

// ============================================
// 空状态
// ============================================
function EmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <Users size={32} className="text-gray-700 mb-2" />
      <p className="text-[12px] text-gray-500 mb-1">暂无团队成员</p>
      <p className="text-[10px] font-mono text-gray-600 mb-3">
        点击「新增成员」创建第一个团队成员
      </p>
      <button
        onClick={onAdd}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-500/15 border border-emerald-500/30 hover:bg-emerald-500/25 transition-colors"
      >
        <Plus size={12} className="text-emerald-400" />
        <span className="text-[10px] font-mono text-emerald-300 uppercase tracking-wider">
          新增成员
        </span>
      </button>
    </div>
  );
}

// ============================================
// 成员负载行
// ============================================
function MemberLoadRow({
  member,
  stat,
  color,
  hasConflict,
  onEdit,
  onDelete,
}: {
  member: TeamMember;
  stat: {
    activeTasks: TaskLite[];
    assignedHours: number;
    loadRate: number;
    cost: number;
  };
  color: string;
  hasConflict: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div
      className={`rounded-md border bg-white/[0.02] p-2.5 ${
        hasConflict
          ? "border-red-500/30 ring-1 ring-red-500/20"
          : "border-white/[0.04]"
      }`}
    >
      <div className="flex items-center gap-3">
        {/* 成员图标 */}
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: `${color}20`, border: `1px solid ${color}40` }}
        >
          <User size={12} style={{ color }} />
        </div>

        {/* 名称与角色 */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] text-gray-200 font-medium truncate">
              {member.name}
            </span>
            {hasConflict && (
              <span className="flex items-center gap-0.5 px-1 rounded text-[8px] font-mono uppercase bg-red-500/15 text-red-400">
                <AlertTriangle size={7} />
                冲突
              </span>
            )}
            <span className="text-[9px] font-mono text-gray-600 truncate">
              {member.role}
            </span>
          </div>
          {/* 负载条 */}
          <div className="mt-1 flex items-center gap-2">
            <div className="flex-1 h-1 rounded-full bg-white/[0.04] overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${Math.min(100, stat.loadRate)}%`, background: color }}
              />
            </div>
            <span
              className="text-[9px] font-mono flex-shrink-0"
              style={{ color }}
            >
              {stat.loadRate}%
            </span>
          </div>
        </div>

        {/* 任务数 */}
        <div className="flex-shrink-0 text-center w-[60px]">
          <div className="text-[8px] font-mono text-gray-600 uppercase">任务</div>
          <div className="text-[12px] font-mono text-gray-300">
            {stat.activeTasks.length}
          </div>
        </div>

        {/* 工时 */}
        <div className="flex-shrink-0 text-center w-[70px]">
          <div className="text-[8px] font-mono text-gray-600 uppercase">工时</div>
          <div className="text-[12px] font-mono text-gray-300">
            {stat.assignedHours}h
          </div>
        </div>

        {/* 成本 */}
        <div className="flex-shrink-0 text-center w-[80px]">
          <div className="text-[8px] font-mono text-gray-600 uppercase">成本</div>
          <div className="text-[12px] font-mono text-amber-400">
            ¥{stat.cost.toLocaleString()}
          </div>
        </div>

        {/* 操作 */}
        <div className="flex items-center gap-0.5 flex-shrink-0">
          <button
            onClick={onEdit}
            className="p-1 rounded hover:bg-white/[0.06] text-gray-500 hover:text-gray-300 transition-colors"
            title="编辑"
          >
            <Pencil size={9} />
          </button>
          <button
            onClick={onDelete}
            className="p-1 rounded hover:bg-white/[0.06] text-gray-500 hover:text-red-400 transition-colors"
            title="删除"
          >
            <Trash2 size={9} />
          </button>
        </div>
      </div>

      {/* 任务详情 */}
      {stat.activeTasks.length > 0 && (
        <div className="mt-2 pt-2 border-t border-white/[0.03] flex flex-wrap gap-1">
          {stat.activeTasks.slice(0, 6).map((t) => {
            const pMeta = PRIORITY_META[t.priority];
            return (
              <span
                key={t.id}
                className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-mono bg-white/[0.03] border border-white/[0.04]"
                title={t.title}
              >
                <span style={{ color: pMeta.color }}>●</span>
                <span className="text-gray-400 truncate max-w-[100px]">
                  {t.title}
                </span>
                <Clock size={7} className="text-gray-600 ml-0.5" />
                <span className="text-gray-600">{formatDate(t.dueDate)}</span>
              </span>
            );
          })}
          {stat.activeTasks.length > 6 && (
            <span className="px-1 py-0.5 text-[9px] font-mono text-gray-600">
              +{stat.activeTasks.length - 6}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================
// 技能矩阵
// ============================================
function SkillMatrix({
  members,
  skills,
  onEdit,
}: {
  members: TeamMember[];
  skills: string[];
  onEdit: (m: TeamMember) => void;
}) {
  if (skills.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-center">
        <Grid3x3 size={28} className="text-gray-700 mb-2" />
        <p className="text-[11px] font-mono text-gray-500">
          暂无技能数据，请在成员编辑中添加技能
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
      <table className="w-full text-[10px] font-mono">
        <thead>
          <tr className="border-b border-white/[0.06]">
            <th className="text-left px-2 py-2 text-gray-500 uppercase tracking-wider sticky top-0 bg-[#0a0e1a]">
              成员
            </th>
            {skills.map((s) => (
              <th
                key={s}
                className="text-center px-2 py-2 text-gray-500 uppercase tracking-wider sticky top-0 bg-[#0a0e1a]"
              >
                {s}
              </th>
            ))}
            <th className="text-center px-2 py-2 text-gray-500 uppercase tracking-wider sticky top-0 bg-[#0a0e1a]">
              操作
            </th>
          </tr>
        </thead>
        <tbody>
          {members.map((m) => (
            <tr
              key={m.id}
              className="border-b border-white/[0.03] hover:bg-white/[0.02]"
            >
              <td className="px-2 py-2">
                <div className="text-gray-200 text-[11px]">{m.name}</div>
                <div className="text-gray-600 text-[9px]">{m.role}</div>
              </td>
              {skills.map((s) => {
                const level = m.skills[s];
                if (!level) {
                  return (
                    <td
                      key={s}
                      className="text-center px-2 py-2 text-gray-700"
                    >
                      —
                    </td>
                  );
                }
                const meta = SKILL_LEVEL_META[level];
                return (
                  <td
                    key={s}
                    className="text-center px-2 py-2"
                    title={`${s}：${meta.label}`}
                  >
                    <span style={{ color: meta.color }}>{meta.symbol}</span>
                  </td>
                );
              })}
              <td className="text-center px-2 py-2">
                <button
                  onClick={() => onEdit(m)}
                  className="p-1 rounded hover:bg-white/[0.06] text-gray-500 hover:text-gray-300 transition-colors"
                  title="编辑"
                >
                  <Pencil size={9} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================
// 成员编辑弹窗
// ============================================
function MemberEditModal({
  member,
  onSubmit,
  onClose,
}: {
  member: TeamMember | null;
  onSubmit: (data: Omit<TeamMember, "id" | "createdAt" | "updatedAt">) => void;
  onClose: () => void;
}) {
  const [form, setForm] = useState({
    name: member?.name || "",
    role: member?.role || "",
    hourlyRate: member?.hourlyRate ?? 200,
    weeklyCapacityHours: member?.weeklyCapacityHours ?? 40,
  });
  // 技能编辑：字符串数组 → 转换为 Record
  const [skillInput, setSkillInput] = useState("");
  const [skills, setSkills] = useState<Record<string, SkillLevel>>(
    member?.skills || {}
  );
  const [error, setError] = useState<string | null>(null);

  const addSkill = () => {
    const name = skillInput.trim();
    if (!name) return;
    if (skills[name]) return;
    setSkills({ ...skills, [name]: "intermediate" });
    setSkillInput("");
  };

  const removeSkill = (name: string) => {
    const next = { ...skills };
    delete next[name];
    setSkills(next);
  };

  const setSkillLevel = (name: string, level: SkillLevel) => {
    setSkills({ ...skills, [name]: level });
  };

  const handleSubmit = () => {
    if (!form.name.trim()) {
      setError("请输入成员姓名");
      return;
    }
    if (!form.role.trim()) {
      setError("请输入角色/职位");
      return;
    }
    if (form.hourlyRate < 0 || form.weeklyCapacityHours < 0) {
      setError("时薪和可用工时不能为负");
      return;
    }
    onSubmit({
      name: form.name,
      role: form.role,
      hourlyRate: form.hourlyRate,
      weeklyCapacityHours: form.weeklyCapacityHours,
      skills,
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-6"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.25 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md max-h-[85vh] flex flex-col rounded-xl border border-emerald-500/30 bg-[#0F1623] overflow-hidden shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
              {member ? (
                <Pencil size={13} className="text-emerald-400" />
              ) : (
                <Plus size={13} className="text-emerald-400" />
              )}
            </div>
            <h3 className="text-[13px] font-semibold text-white">
              {member ? "编辑成员" : "新增成员"}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-white/[0.06] text-gray-400 hover:text-white transition-colors"
          >
            <X size={14} />
          </button>
        </div>

        {/* 表单 */}
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {error && (
            <div className="px-3 py-2 rounded border border-red-500/30 bg-red-500/10 text-[10px] text-red-300 font-mono">
              {error}
            </div>
          )}

          <FormField label="成员姓名">
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="输入成员姓名"
              className="w-full px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 placeholder-gray-600 outline-none focus:border-emerald-500/30"
            />
          </FormField>

          <FormField label="角色 / 职位">
            <input
              type="text"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              placeholder="如：前端工程师"
              className="w-full px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 placeholder-gray-600 outline-none focus:border-emerald-500/30"
            />
          </FormField>

          <div className="grid grid-cols-2 gap-3">
            <FormField label="时薪（元/小时）">
              <input
                type="number"
                min={0}
                value={form.hourlyRate}
                onChange={(e) =>
                  setForm({
                    ...form,
                    hourlyRate: Math.max(0, Number(e.target.value) || 0),
                  })
                }
                className="w-full px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 outline-none focus:border-emerald-500/30"
              />
            </FormField>
            <FormField label="周可用工时">
              <input
                type="number"
                min={0}
                max={168}
                value={form.weeklyCapacityHours}
                onChange={(e) =>
                  setForm({
                    ...form,
                    weeklyCapacityHours: Math.max(
                      0,
                      Math.min(168, Number(e.target.value) || 0)
                    ),
                  })
                }
                className="w-full px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 outline-none focus:border-emerald-500/30"
              />
            </FormField>
          </div>

          {/* 技能编辑 */}
          <FormField label="技能清单">
            <div className="flex items-center gap-1.5 mb-2">
              <input
                type="text"
                value={skillInput}
                onChange={(e) => setSkillInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addSkill();
                  }
                }}
                placeholder="输入技能名后回车添加"
                className="flex-1 px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 placeholder-gray-600 outline-none focus:border-emerald-500/30"
              />
              <button
                onClick={addSkill}
                className="px-2 py-1.5 rounded-md bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[10px] font-mono hover:bg-emerald-500/25 transition-colors"
              >
                添加
              </button>
            </div>
            <div className="space-y-1 max-h-[140px] overflow-y-auto">
              {Object.keys(skills).length === 0 ? (
                <div className="text-[9px] font-mono text-gray-600 text-center py-2">
                  暂无技能，添加第一个技能
                </div>
              ) : (
                Object.entries(skills).map(([name, level]) => (
                  <div
                    key={name}
                    className="flex items-center gap-1.5 px-2 py-1 rounded bg-white/[0.02] border border-white/[0.04]"
                  >
                    <span className="text-[10px] text-gray-300 flex-1 truncate">
                      {name}
                    </span>
                    <select
                      value={level}
                      onChange={(e) =>
                        setSkillLevel(name, e.target.value as SkillLevel)
                      }
                      className="px-1 py-0.5 rounded bg-white/[0.04] border border-white/[0.06] text-[9px] font-mono text-gray-300 outline-none cursor-pointer"
                    >
                      <option value="beginner">入门</option>
                      <option value="intermediate">熟练</option>
                      <option value="expert">专家</option>
                    </select>
                    <button
                      onClick={() => removeSkill(name)}
                      className="p-0.5 rounded hover:bg-white/[0.06] text-gray-500 hover:text-red-400 transition-colors"
                    >
                      <X size={10} />
                    </button>
                  </div>
                ))
              )}
            </div>
          </FormField>
        </div>

        {/* 底部按钮 */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-white/[0.06] bg-white/[0.01]">
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded-md border border-white/[0.06] text-[10px] font-mono text-gray-400 hover:bg-white/[0.04] transition-colors"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-emerald-500/20 border border-emerald-500/30 text-[10px] font-mono text-emerald-300 hover:bg-emerald-500/30 transition-colors"
          >
            <DollarSign size={10} />
            {member ? "保存" : "创建"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============================================
// 表单字段包装
// ============================================
function FormField({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div>
      <label className="block text-[9px] font-mono text-gray-500 uppercase tracking-wider mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}
